import {
  type ChangeEventHandler,
  type FocusEventHandler,
  forwardRef,
} from 'react'
import { Box, type BoxProps } from '../Box'
import { inputBase, inputStyle, mediumInput, smallInput } from './Input.css'

const inputSizes = {
  small: smallInput,
  medium: mediumInput,
} as const

export type InputSize = keyof typeof inputSizes
export type OnChange = ChangeEventHandler<HTMLInputElement>
export type OnFocus = FocusEventHandler<HTMLInputElement>

interface InputProps {
  onChange?: OnChange
  onBlur?: OnFocus
  onFocus?: OnFocus
  unstyled?: boolean
  unspaced?: boolean
  size?: InputSize
}

type Props = Omit<BoxProps, keyof InputProps | 'children'> & InputProps

export type { Props as InputProps }

export const inputDefaultSize: InputSize = 'medium'

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      type = 'text',
      className,
      unstyled,
      unspaced,
      size = inputDefaultSize,
      ...props
    },
    ref,
  ) => {
    return (
      <Box
        as="input"
        type={type}
        width="100%"
        textAlign="inherit"
        ref={ref}
        className={[
          className,
          {
            [inputBase]: unstyled,
            [inputSizes[size]]: !unspaced,
            [inputStyle]: !unstyled,
          },
        ]}
        {...props}
      />
    )
  },
)

Input.displayName = 'Input'

interface LabelProps {
  name?: string
}

type LProps = Omit<BoxProps, keyof LabelProps> & LabelProps

export const Label = forwardRef<HTMLLabelElement, LProps>(
  ({ name, htmlFor, ...props }, ref) => {
    return <Box as="label" htmlFor={htmlFor ?? name} {...props} ref={ref} />
  },
)

Label.displayName = 'Label'
